import { Eye, EyeOff, Building2 } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { AuthPageLayout } from '../../components/layout/AuthPageLayout';
import { supabase } from '../../lib/supabase';
import {
  trackSignupPageVisit,
  trackSignupFormStart,
  trackSignupComplete,
  trackSignupAbandon,
} from '../../utils/signupTracking';
import { getSignupErrorMessage } from '../../utils/authErrorMessages';
import {
  PASSWORD_POLICY_PLACEHOLDER,
  validatePassword,
} from '@rollops/app-shared/utils/passwordPolicy';
import { PasswordRulesList } from '../../components/auth/PasswordRulesList';
import { useOnboardingStore } from '../../store/onboarding.store';

// Toast hook similar to mobile app
function useToast(duration = 3000) {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setToast({ message, type });
    timeoutRef.current = setTimeout(() => setToast(null), duration);
  };

  return { toast, showToast };
}

export default function OwnerSignup() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { toast, showToast } = useToast();

  // Track signup page visit
  useEffect(() => {
    trackSignupPageVisit('owner');
    
    // Track abandon on page unload
    const handleBeforeUnload = () => {
      trackSignupAbandon();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also track abandon when component unmounts (navigation away)
      // But only if signup wasn't completed
      const existingData = localStorage.getItem('rollops_signup_tracking');
      if (existingData) {
        const data = JSON.parse(existingData);
        if (!data.completed) {
          trackSignupAbandon();
        }
      }
    };
  }, []);

  // Track form start when user starts typing email
  useEffect(() => {
    if (email.trim().length > 0) {
      trackSignupFormStart('owner', email.trim());
    }
  }, [email]);

  // Check if user is already authenticated
  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Check if they have incomplete onboarding
          const { data: relationships } = await supabase
            .from('user_company_relationships')
            .select('company_id, companies(setup_complete)')
            .eq('user_id', session.user.id);

          if (relationships && relationships.length > 0) {
            const hasIncompleteSetup = relationships.some(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (rel: any) => !rel.companies?.setup_complete
            );
            if (hasIncompleteSetup) {
              navigate('/onboarding');
            } else {
              navigate('/dashboard');
            }
          } else {
            // No company yet, go to onboarding
            navigate('/onboarding');
          }
        }
      } catch {
        // Failed to check auth session
      }
    };
    checkUser();
  }, [navigate]);

  const validateForm = () => {
    if (!firstName.trim()) {
      showToast('First name is required.', 'error');
      return false;
    }
    if (!lastName.trim()) {
      showToast('Last name is required.', 'error');
      return false;
    }
    if (!email.trim()) {
      showToast('Email is required.', 'error');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast('Please enter a valid email address.', 'error');
      return false;
    }
    if (!password) {
      showToast('Password is required.', 'error');
      return false;
    }
    const passwordCheck = validatePassword(password);
    if (!passwordCheck.ok) {
      showToast(passwordCheck.reason, 'error');
      return false;
    }
    if (password !== confirmPassword) {
      showToast('Passwords do not match.', 'error');
      return false;
    }
    if (!acceptTerms) {
      showToast('Please accept the Terms of Service.', 'error');
      return false;
    }
    return true;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Sign up with Supabase Auth
      // Use 'new_owner' to match database trigger (for email confirmation flows)
      // If session is immediate, we'll create company atomically via RPC instead
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            signup_intent: 'new_owner', // Matches database trigger for email confirmation flows
            user_type: 'owner',
          },
        },
      });

      if (signUpError) {
        showToast(getSignupErrorMessage(signUpError), 'error');
        setLoading(false);
        return;
      }

      if (!data.user) {
        showToast('Account creation failed. Please try again.', 'error');
        setLoading(false);
        return;
      }

      // Track successful signup completion
      trackSignupComplete('owner', email.trim(), firstName.trim(), lastName.trim());

      // Send welcome email (fire and forget - don't block signup flow)
      try {
        const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
          body: {
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            signupType: 'owner',
          },
        });
        if (emailError) {
          // Don't fail signup if email fails
        }
      } catch {
        // Don't fail signup if email fails
      }

      // Check if email confirmation is required
      if (data.session) {
        // Logged in immediately, proceed to onboarding
        showToast('Account created successfully!', 'success');
        
        // Create user profile
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            id: data.user.id,
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim(),
          });

        if (profileError) {
          // Profile may already exist via trigger, continue anyway
        }

        // CRITICAL: Use atomic RPC function to create draft company immediately
        // This ensures: company + user_company_relationship + last_selected_company are created atomically
        // JWT will always be valid because the relationship exists immediately
        try {
          const companyName = `${firstName.trim() || 'My'}'s New Gym`;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: companyId, error: rpcError } = await (supabase.rpc as any)('create_company_for_user', {
            p_name: companyName,
            p_slogan: null,
            p_ein: null,
            p_contact_fn: firstName.trim() || null,
            p_contact_ln: lastName.trim() || null,
            p_company_email: email.trim() || null,
            p_phone: null,
            p_address: null,
            p_address2: null,
            p_city: null,
            p_state: null,
            p_zip: null,
            p_logo_url: null,
          });

          if (rpcError) {
            showToast('Account created but failed to create gym. Please try again.', 'error');
            // Still navigate to onboarding - they can create company there
          } else if (companyId) {
            // Store companyId in onboarding store so onboarding can find it
            useOnboardingStore.getState().setCompanyId(companyId as string);
            // Refresh JWT to get new company_id and user_role claims
            await supabase.auth.refreshSession();
          }
        } catch {
          // Continue to onboarding - they can create company there if RPC failed
        }

        // Short delay to show success message
        setTimeout(() => {
          navigate('/onboarding');
        }, 1000);
      } else {
        // Email confirmation required
        showToast('Please check your email to confirm your account.', 'success');
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      }
    } catch (err) {
      showToast('An unexpected error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout showBackButton backTo="/" backLabel="Landing page">
      <Helmet>
        <title>Gym Owner Sign Up - RollOps</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
        <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>

      <div className="w-full max-w-md flex flex-col items-center">
        {/* Logo and Brand */}
        <Link
          to="/"
          className="flex items-center space-x-4 mb-8 hover:opacity-80 transition-opacity"
        >
          <img 
            src="/rollops-logo.png" 
            alt="RollOps Logo" 
            className="w-20 h-20 sm:w-24 sm:h-24"
          />
          <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
        </Link>

        {/* Owner Badge */}
        <div className="flex items-center gap-2 mb-6 bg-[#9B1421]/20 px-4 py-2 rounded-full border border-[#9B1421]/40">
          <Building2 size={18} className="text-text-primary" />
          <span className="text-sm font-medium text-[#EBFFEE]">Gym Owner Registration</span>
        </div>

        {/* Signup Card */}
        <Card className="w-full border-[#504A4A] bg-[#221F1F] shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold text-center text-[#EBFFEE]">
              Create Your Account
            </CardTitle>
            <p className="text-sm text-[#A9A3A3] text-center">
              Set up your gym management platform
            </p>
            <div className="bg-[#9B1421]/20 border border-[#9B1421]/40 rounded-lg p-3 mt-4">
              <p className="text-xs text-[#A9A3A3] text-center">
                <strong className="text-[#EBFFEE]">30-Day Free Trial</strong> - No credit card required to start.{' '}
                <span className="text-[#EBFFEE]">Enabling subscription payments requires a paid subscription.</span>
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignup} className="space-y-4">
              {/* Name Row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-[#A9A3A3] font-medium">First Name</label>
                  <input
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 rounded-md text-base bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                    autoComplete="given-name"
                    required
                    maxLength={30}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#A9A3A3] font-medium">Last Name</label>
                  <input
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 rounded-md text-base bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                    autoComplete="family-name"
                    required
                    maxLength={30}
                  />
                </div>
              </div>

              {/* Email Input */}
              <div className="space-y-1">
                <label className="text-xs text-[#A9A3A3] font-medium">Email Address</label>
                <input
                  type="email"
                  placeholder="john@yourgym.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-md text-base bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                  autoCapitalize="none"
                  autoComplete="email"
                  required
                  maxLength={254}
                />
              </div>

              {/* Password Input */}
              <div className="space-y-1">
                <label className="text-xs text-[#A9A3A3] font-medium">Password</label>
                <div className="flex items-center rounded-md bg-[#504A4A] pr-2 transition-colors hover:bg-[#5A5454] focus-within:ring-2 focus-within:ring-[#9B1421]/50">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={PASSWORD_POLICY_PLACEHOLDER}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-md text-base bg-transparent text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none"
                    autoComplete="new-password"
                    required
                    aria-describedby="password-rules"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 text-[#EBFFEE] hover:text-[#A9A3A3] transition-colors cursor-pointer"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <PasswordRulesList id="password-rules" password={password} />
              </div>

              {/* Confirm Password Input */}
              <div className="space-y-1">
                <label className="text-xs text-[#A9A3A3] font-medium">Confirm Password</label>
                <div className="flex items-center rounded-md bg-[#504A4A] pr-2 transition-colors hover:bg-[#5A5454] focus-within:ring-2 focus-within:ring-[#9B1421]/50">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-md text-base bg-transparent text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="p-2 text-[#EBFFEE] hover:text-[#A9A3A3] transition-colors cursor-pointer"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3 py-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="w-4 h-4 mt-1 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421] focus:ring-offset-0"
                />
                <label htmlFor="terms" className="text-sm text-[#A9A3A3] leading-tight">
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#EBFFEE] hover:text-[#A9A3A3] underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-[#EBFFEE] hover:text-[#A9A3A3] underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {/* Signup Button */}
              <Button
                type="submit"
                variant="red"
                size="lg"
                disabled={loading}
                className="w-full font-semibold"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>

              {/* Login Link */}
              <div className="flex justify-center pt-2">
                <span className="text-sm text-[#A9A3A3]">
                  Already have an account?{' '}
                  <Link
                    to="/signin"
                    className="text-[#EBFFEE] hover:text-[#A9A3A3] font-medium transition-colors"
                  >
                    Log In
                  </Link>
                </span>
              </div>

              {/* Student Signup Link */}
              <div className="flex justify-center border-t border-[#504A4A] pt-4 mt-4">
                <span className="text-sm text-[#A9A3A3]">
                  Looking to join a gym?{' '}
                  <Link
                    to="/signup"
                    className="text-[#EBFFEE] hover:text-[#A9A3A3] font-medium transition-colors"
                  >
                    Student Sign Up
                  </Link>
                </span>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-8 left-0 right-0 flex items-center justify-center px-6 z-50 pointer-events-none">
          <div
            className={`px-6 py-4 rounded-lg text-center font-semibold max-w-md shadow-lg ${
              toast.type === 'error' 
                ? 'bg-[#9B1421] text-white' 
                : 'bg-green-600 text-white'
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
    </AuthPageLayout>
  );
}

